<template>
  <div class="channel-sales">
    <div class="module-header">
      <h3 class="module-title">渠道销售占比</h3>
      <div class="time-selector">
        <button 
          v-for="option in timeOptions" 
          :key="option.value"
          :class="['time-btn', { active: channelSales.period === option.value }]"
          @click="changePeriod(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>
    <div class="chart-container">
      <div ref="chartRef" class="chart"></div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import * as echarts from 'echarts'

export default {
  name: 'ChannelSales',
  data() {
    return {
      chart: null,
      timeOptions: [
        { label: '今日', value: 'today' },
        { label: '本周', value: 'week' },
        { label: '本月', value: 'month' }
      ]
    }
  },
  computed: {
    ...mapState(['channelSales']),
    // 添加安全的计算属性
    safeChannels() {
      return this.channelSales?.channels || []
    }
  },
  mounted() {
    this.initChart()
    // 初次渲染：若已有数据，立即绘制一次，避免仅依赖 watcher
    this.$nextTick(() => {
      if (this.channelSales && Array.isArray(this.channelSales.channels) && this.channelSales.channels.length > 0) {
        this.updateChart()
      }
    })
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
    }
  },
  watch: {
    'channelSales.channels': {
      handler() {
        // 添加安全检查
        if (this.channelSales && Array.isArray(this.channelSales.channels)) {
          this.updateChart()
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions(['fetchChannelSales']),
    initChart() {
      if (!this.$refs.chartRef) {
        console.error('ChannelSales: chartRef not found')
        return
      }
      
      try {
        this.chart = echarts.init(this.$refs.chartRef)
      } catch (error) {
        console.error('ChannelSales: Failed to initialize ECharts', error)
        return
      }
      
      const resizeHandler = () => {
        if (this.chart) {
          this.chart.resize()
        }
      }
      
      window.addEventListener('resize', resizeHandler)
      
      // 清理事件监听器
      this.$once('hook:beforeDestroy', () => {
        window.removeEventListener('resize', resizeHandler)
      })
    },
    updateChart() {
      // 添加安全检查
      if (!this.chart) {
        console.error('ChannelSales: Chart instance not found')
        return
      }
      
      if (!this.channelSales || !Array.isArray(this.channelSales.channels) || this.channelSales.channels.length === 0) {
        return
      }
      // 规范化名称映射到四大渠道
      const normalize = (name) => {
        if (!name) return ''
        if (name.includes('线下零售')) return '线下零售'
        if (name.includes('线下批销')) return '线下批销'
        if (name.includes('第三') || name.includes('第三方')) return '第三方线上'
        if (name.includes('自营') || name.includes('平台')) return '自营平台'
        return name
      }
      const order = ['第三方线上', '自营平台', '线下零售', '线下批销']
      const colorMap = {
        '线下零售': 'rgba(255, 225, 143, 0.6)',
        '第三方线上': 'rgba(0, 255, 156, 0.6)',
        '自营平台': 'rgba(52, 236, 255, 0.6)',
        '线下批销': 'rgba(255, 102, 204, 0.6)'
      }
      // 用于外层分段环的发光阴影颜色
      const glowMap = {
        '线下零售': '#ffe18f',
        '第三方线上': '#00ff9c',
        '自营平台': '#34ecff',
        '线下批销': '#ff66cc'
      }
      const percentMap = { '线下零售': 0, '第三方线上': 0, '自营平台': 0, '线下批销': 0 }
      this.channelSales.channels.forEach(ch => {
        const key = normalize(ch.channelName)
        if (percentMap[key] !== undefined) {
          percentMap[key] = Number(ch.percentage) || 0
        }
      })

      const gap = 1 // 间隔角度占比，制造分隔效果
      const ringData = []
      
      // 统计有效数据项数量（value > 0的项）
      const validDataCount = order.filter(name => Math.max(0, percentMap[name]) > 0).length
      
      order.forEach((name, index) => {
        // 当数据值为0时，隐藏对应的扇形区域显示
        const value = Math.max(0, percentMap[name])
        if (value > 0) {
          ringData.push({
            name,
            value: value,
            itemStyle: {
              color: colorMap[name],
              borderColor: glowMap[name],
              borderWidth: 2,
              shadowBlur: 10,
              shadowColor: colorMap[name],
              opacity: 0.96
            }
          })
          
          // 只有当有效数据项大于1个时才添加间隔
          if (validDataCount > 1) {
            ringData.push({
              name: `${name}_gap`,
              value: gap,
              itemStyle: { color: 'transparent' },
              tooltip: { show: false },
              label: { show: false }
            })
          }
        }
      })

      const myChart = this.chart;

      const option = {
        tooltip: { show: false },
        legend: { show: false },
        series: [
          // 外层分段环
          {
            name: '渠道占比',
            type: 'pie',
            radius: ['55%', '64%'],
            center: ['50%', '48%'],
            startAngle: 90,
            clockwise: true,
            minAngle: 5,
            avoidLabelOverlap: true,
            label: {
              show: true,
              position: 'outside',
              formatter: function(params) {
                // 跳过间隔片段标签
                if (params.name && params.name.endsWith('_gap')) return ''
                var v = Number(params.value) || 0
                var val = v > 0 ? (v == 100 ? '100%' : v.toFixed(2) + '%') : '0%'
                var name = params.name || ''
                return '{val|' + val + '}\n{icon| } \n{name|' + name + '}'
              },
              padding: 0,
              distanceToLabelLine: 0,
              rich: {
                icon: {
                  width: 46,
                  height: 46,
                  backgroundColor: { image: require('@/assets/images/gd_icon.png') },
                  lineHeight: 20,
                },
                val: {
                  fontFamily: 'Microsoft YaHei',
                  fontSize: 16,
                  fontWeight: 700,
                  color: '#EDB331',
                  align: 'center',
                  lineHeight: 20,
                },
                name: {
                  fontFamily: 'Microsoft YaHei',
                  fontSize: 12,
                  fontWeight: 400,
                  color: '#FFFFFF',
                  align: 'center',
                  lineHeight: 20,
                  padding: [10, 0, 0, 0]
                }
              }
            },
            labelLine: {
              show: true,
              length: 10,
              length2: 0,
              maxSurfaceAngle: 60,
              smooth: false,
              // lineStyle: { color: '#506690', width: 1 }
              lineStyle: { width: 1 }
            },
            labelLayout: function (params) {
              // 跳过间隔片段标签和undefined内容
              if (!params.text) {
                return null;
              }
              
              // 从text中提取name值，格式如："{val|0.57%}\n{icon| }\n{name|自营平台}"
              let labelName = '';
              const nameMatch = params.text.match(/\{name\|([^}]+)\}/);
              if (nameMatch && nameMatch[1]) {
                labelName = nameMatch[1];
              } else {
                // 如果没有匹配到name，跳过此标签
                return null;
              }
              
              const chartWidth = myChart.getWidth();
              const chartHeight = myChart.getHeight();
              const centerX = chartWidth / 2;
              const centerY = chartHeight * 0.48; // 饼图中心Y位置
              
              const points = params.labelLinePoints;
              
              // 根据标签名称设置固定位置
              if (labelName === '第三方线上') {
                // 第三方线上：左侧上方
                const targetX = chartWidth * 0.20; // 左侧20%位置
                const targetY = centerY - 50; // 中心上方50px
                
                points[1] = [centerX - 80, targetY]; // 引导线中间点
                points[2] = [targetX - 18, targetY]; // 标签位置
                
                return {
                  labelLinePoints: points,
                  x: targetX - 50,
                  y: targetY
                };
              } else if (labelName === '自营平台') {
                // 自营平台：左侧下方
                const targetX = chartWidth * 0.20; // 左侧20%位置
                const targetY = centerY + 50; // 中心下方50px
                
                points[1] = [centerX - 80, targetY]; // 引导线中间点
                points[2] = [targetX - 18, targetY]; // 标签位置
                
                return {
                  labelLinePoints: points,
                  x: targetX -50,
                  y: targetY
                };
              } else if (labelName === '线下零售') {
                if (validDataCount === 1) {
                  // 只有一个线下零售：左侧中间位置
                  const targetX = chartWidth * 0.22; // 左侧22%位置
                  const targetY = centerY; // 垂直居中
                  
                  points[1] = [centerX - 80, targetY]; // 引导线中间点
                  points[2] = [targetX - 18, targetY]; // 标签位置
                  
                  return {
                    labelLinePoints: points,
                    x: targetX,
                    y: targetY
                  };
                }
                // 线下零售：右侧上方
                const targetX = chartWidth * 0.80; // 右侧80%位置
                const targetY = centerY - 50; // 中心上方50px
                
                points[1] = [centerX + 80, targetY]; // 引导线中间点
                points[2] = [targetX + 18, targetY]; // 标签位置
                
                return {
                  labelLinePoints: points,
                  x: targetX,
                  y: targetY
                };
              } else if (labelName === '线下批销') {
                // 线下批销：右侧下方
                const targetX = chartWidth * 0.80; // 右侧80%位置
                const targetY = centerY + 50; // 中心下方50px
                
                points[1] = [centerX + 80, targetY]; // 引导线中间点
                points[2] = [targetX + 18, targetY]; // 标签位置
                
                return {
                  labelLinePoints: points,
                  x: targetX + (validDataCount > 1 ? 50 : 0),
                  y: targetY
                };
              }
            },
            data: ringData,
            emphasis: { disabled: true }
          },
          // 内圈 粗描边（基础轮廓）
          {
            name: 'inner-border',
            type: 'pie',
            radius: ['46%', '46%'], // 使用同值半径，仅渲染边框
            center: ['50%', '48%'],
            silent: true,
            label: { show: false },
            labelLine: { show: false },
            itemStyle: {
              color: 'transparent',
              borderColor: '#45D3FF',
              borderWidth: 3,
              shadowBlur: 20,
              shadowColor: 'rgba(69, 211, 255, 0.75)'
            },
            emphasis: { disabled: true },
            data: [{ value: 100 }]
          },
          // 粗边框内部的指定颜色渐变光晕（边缘亮到中心透明）
          {
            name: 'inner-fade',
            type: 'pie',
            radius: ['2%', '46%'],
            center: ['50%', '48%'],
            silent: true,
            label: { show: false },
            labelLine: { show: false },
            itemStyle: {
              color: {
                type: 'radial',
                x: 0.5, y: 0.5, r: 0.9,
                colorStops: [
                  { offset: 0.0, color: 'rgba(69, 211, 255, 0.0)' }, // 中心透明
                  { offset: 0.2, color: 'rgba(69, 211, 255, 0.05)' }, // 中心透明
                  { offset: 0.9, color: 'rgba(69, 211, 255, 0.30)' },
                  { offset: 1.0, color: 'rgba(69, 211, 255, 0.55)' } // 靠近粗边框更亮
                ]
              },
              shadowBlur: 30,
              shadowColor: 'rgba(69, 211, 255, 0.65)'
            },
            emphasis: { disabled: true },
            data: [{ value: 100 }]
          }
        ],
      }

      try {
        this.chart.setOption(option, true)
      } catch (error) {
        console.error('ChannelSales: Failed to update chart', error)
      }
    },
    async changePeriod(period) {
      if (period !== this.channelSales.period) {
        await this.fetchChannelSales(period)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.channel-sales {
  height: 100%;
  display: flex;
  flex-direction: column;

  .module-header {
    margin-bottom: 0;
  }

  .chart-container {
    box-sizing: border-box;
    flex: 1;
    min-height: 170px;
    display: flex;
    flex-direction: column;
    position: relative;

    .chart {
      width: 100%;
      height: 206px;
    }
  }
}
</style>